import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { APIURL } from '../../../configApi/apiConfig';
import { UsersIcon, PhoneIcon, MapPinIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useSip } from '../../SipContext';

/**
 * Componente que obtiene y muestra las referencias del cliente.
 * Intenta buscar primero por sCre_SolicitudWeb (UUID), luego por cédula y finalmente por idCompra.
 * 
 * @param {Object} props
 * @param {string|number} props.idCompra - ID de la compra (fallback)
 * @param {string} props.sCre_SolicitudWeb - UUID de la solicitud (preferido)
 * @param {string} props.cedula - Cédula del cliente (fallback)
 */
export const ReferenciasResumen = ({ idCompra, sCre_SolicitudWeb, cedula }) => {
    const [referencias, setReferencias] = useState([]);
    const [loading, setLoading] = useState(true);
    const { makeCall, phoneAvailable } = useSip();

    useEffect(() => {
        const fetchReferencias = async () => {
            if (!idCompra && !sCre_SolicitudWeb && !cedula) {
                setLoading(false);
                return;
            }
            try {
                const token = localStorage.getItem('token');
                const url = sCre_SolicitudWeb 
                    ? APIURL.get_cre_referenciasclientesweb_uuid(sCre_SolicitudWeb)
                    : cedula 
                        ? APIURL.get_cre_referenciasclientesweb_cedula(cedula)
                        : APIURL.get_cre_referenciasclientesweb_id(idCompra);
                const response = await axios.get(url, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                if (Array.isArray(response.data) && response.data.length > 0) {
                    setReferencias(response.data);
                } else if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
                    setReferencias(response.data.data);
                } else {
                    setReferencias([]);
                }
            } catch (error) {
                console.error("Error al cargar referencias:", error);
                setReferencias([]);
            } finally {
                setLoading(false);
            }
        };

        fetchReferencias();
    }, [idCompra, sCre_SolicitudWeb, cedula]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <div className="relative w-12 h-12">
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full opacity-20 animate-pulse"></div>
                    <div className="absolute inset-2 border-4 border-transparent border-t-orange-500 border-r-amber-500 rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }

    if (!Array.isArray(referencias) || referencias.length === 0) {
        return (
            <div className="p-12 text-center border-2 border-dashed border-slate-300 rounded-2xl bg-white text-slate-500 flex flex-col items-center justify-center min-h-[250px] animate-fadeIn mt-4 max-w-5xl mx-auto">
                <UsersIcon className="w-16 h-16 mb-4 text-slate-300" />
                <p className="font-bold uppercase tracking-widest">Sin Referencias</p>
                <p className="text-sm mt-2">No se encontraron referencias para este cliente.</p>
            </div>
        );
    }

    return (
        <div className="bg-transparent overflow-hidden p-2 mt-4 max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-6 px-2">
                <div className="p-3 bg-gradient-to-br from-orange-100 to-amber-100 text-orange-600 rounded-xl shadow-sm">
                    <UsersIcon className="w-6 h-6" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Referencias del Cliente</h2>
                    <p className="text-slate-500 text-sm font-medium">{referencias.length} referencia(s) registrada(s)</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.isArray(referencias) && referencias.map((ref, index) => (
                    <div key={index} className="bg-white rounded-2xl border-2 border-slate-100 p-5 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 relative group">
                        <div className="absolute top-0 right-0 p-3">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200">
                                <ShieldCheckIcon className="w-3 h-3" />
                                {ref.TipoReferencia || 'Referencia'}
                            </span>
                        </div>
                        
                        <div className="flex items-center gap-4 mb-5">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-lg shadow-inner">
                                {ref.NombresApellidos ? ref.NombresApellidos.charAt(0).toUpperCase() : 'R'}
                            </div>
                            <div>
                                <h3 className="font-bold text-slate-800 leading-tight uppercase pr-12 line-clamp-2">
                                    {ref.NombresApellidos || 'No Especificado'}
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5 capitalize">{ref.Parentesco || 'Parentesco no especificado'}</p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {ref.Telefono && (
                                <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="p-1.5 bg-white rounded-lg shadow-sm">
                                        <PhoneIcon className="w-4 h-4 text-slate-500" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase">Teléfono Principal</p>
                                        <p className="text-sm font-semibold text-slate-700">{ref.Telefono}</p>
                                    </div>
                                    {phoneAvailable && (
                                        <button 
                                            onClick={() => makeCall(ref.Telefono, idCompra, { clientName: ref.NombresApellidos })}
                                            className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                                            title="Llamar a esta referencia"
                                        >
                                            <PhoneIcon className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            )}

                            {ref.Celular && ref.Celular !== ref.Telefono && (
                                <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="p-1.5 bg-white rounded-lg shadow-sm">
                                        <PhoneIcon className="w-4 h-4 text-slate-500" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase">Celular</p>
                                        <p className="text-sm font-semibold text-slate-700">{ref.Celular}</p>
                                    </div>
                                    {phoneAvailable && (
                                        <button 
                                            onClick={() => makeCall(ref.Celular, idCompra, { clientName: ref.NombresApellidos })}
                                            className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                                            title="Llamar a este celular"
                                        >
                                            <PhoneIcon className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            )}

                            {(ref.Direccion || ref.Sector || ref.Ciudad) && (
                                <div className="flex items-start gap-3 p-2.5 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="p-1.5 bg-white rounded-lg shadow-sm mt-0.5">
                                        <MapPinIcon className="w-4 h-4 text-slate-500" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase">Ubicación</p>
                                        <p className="text-xs text-slate-700 leading-relaxed uppercase">
                                            {[ref.Ciudad, ref.Sector, ref.Direccion].filter(Boolean).join(' - ')}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
